// @ts-nocheck
import * as Sentry from "@sentry/nextjs";
import { useDetailedUser } from "ui/hooks/useDetailedUsers";
import { produce } from "immer";
import { useRouter } from "next/router";
import { useRoomAndUser } from "queries/chat/rooms/hooks";
import { RoomResponse } from "queries/chat/rooms/types";
import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useChat } from "ui/components/chat/container";
import { roomFlagsAreMuted } from "ui/lib/helpers";
import { handleUpdateRequest } from "ui/lib/useRequest";

import { createContainer } from "unstated-next";

interface User {
  id: string;
  coachRoomID?: string;
  roomFlagsAreActive?: boolean;
}

interface UpdateActionFlagParams {
  value: string;
}

const useContainer = () => {
  const [hasError, setHasError] = useState<boolean>(false);
  const { selectedUserId, roomDictionary, activeRoomID, mutate } = useChat();
  const { query } = useRouter();
  const queryClient = useQueryClient();

  const {
    status,
    data: response,
    error,
    isFetching,
  } = useDetailedUser(
    {
      active: !!selectedUserId,
      filter: {
        id: { eq: selectedUserId },
      },
      node: {
        id: true,
        coachRoomID: true,
        // roomFlagsAreActive: true,
      },
    },
    [selectedUserId]
  );

  const user: User | null = useMemo(() => {
    if (status !== "success") return null;
    if (!response) return null;
    if (!response?.detailedUsers?.edges?.length) return null;
    const [{ node }] = response?.detailedUsers?.edges || [{}];
    return node as User;
  }, [response, status]);

  const { data: roomResponse } = useRoomAndUser(
    user?.coachRoomID as string,
    roomDictionary[activeRoomID as string]
  );
  const room = useMemo(() => roomResponse?.room, [roomResponse]);

  const updateActionFlag = async ({
    value: actionFlag,
  }: UpdateActionFlagParams) => {
    try {
      mutate((current) => {
        return produce(current, (draft) => {
          const channels = draft?.groups?.filter?.(
            ({ id }) => id === query.bootcampId
          );
          const roomId = user?.coachRoomID;

          channels?.forEach(({ sections }) =>
            sections.forEach((section) => {
              section.rooms?.forEach((room) => {
                if (roomId === room?.id) {
                  room.actionFlag = actionFlag;
                }
              });
            })
          );
        });
      });

      // Update the cached room data
      queryClient.setQueryData<RoomResponse | undefined>(
        ["room", user?.coachRoomID],
        (oldData) => {
          return oldData
            ? { ...oldData, room: { ...oldData.room, actionFlag } }
            : oldData;
        }
      );

      if (room?.id) {
        await handleUpdateRequest(
          `v1/updateLegacyRoom`,
          JSON.stringify({ actionFlag, roomID: room.id }),
          "post"
        );
      }
    } catch (error) {
      console.error(error);
      setHasError(true);
      Sentry.captureException(error);
    }
  };

  const isMuted = useMemo(() => {
    const flagsAreMuted = roomFlagsAreMuted(room?.flagSettings);
    if (flagsAreMuted) return true;
    return typeof room?.flagsAreActive === "boolean" && !room?.flagsAreActive;
  }, [room]);

  return {
    isMuted,
    updateActionFlag,
    hasError,
    room,
  };
};

export const {
  useContainer: useFlaggingSystem,
  Provider: FlaggingSystemProvider,
} = createContainer(useContainer);
